/**
 * @typedef {Object} FormData
 * @property {number} currentStep
 * @property {Object} stepData
 */

class B2BFormStepper extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.totalSteps = 0;
    this.webhookUrl = '';
    this.uploadedFiles = [];
    this.validationMessages = {};
  }

  connectedCallback() {
    this.webhookUrl = this.dataset.webhookUrl || '';
    this.loadValidationMessages();
    this.init();
  }

  /**
   * Load validation messages from JSON
   * @returns {void}
   */
  loadValidationMessages() {
    const messagesScript = document.querySelector('[data-b2b-validation-messages]');
    if (!messagesScript) return;

    try {
      this.validationMessages = JSON.parse(messagesScript.textContent);
    } catch (error) {
      this.validationMessages = {};
    }
  }

  /**
   * Initialize component
   * @returns {void}
   */
  init() {
    this.steps = this.querySelectorAll('.b2b-form__step');
    this.stepperItems = this.querySelectorAll('.b2b-stepper__item');
    this.totalSteps = this.steps.length;

    this.bindEvents();
    this.initializeRadioToggles();
    this.initializeFileUpload();
    this.initializeRealtimeValidation();
    this.initializeContactMirroring();
    this.initializeScheduleClosedToggle();
  }

  /**
   * Mirror responsable fields to acheteur + compta when checkbox checked.
   * @returns {void}
   */
  initializeContactMirroring() {
    const mirrorMap = {
      acheteur: {
        responsable_nom: 'acheteur_nom',
        responsable_prenom: 'acheteur_prenom',
        responsable_email: 'acheteur_email',
        responsable_telephone: 'acheteur_telephone',
      },
      compta: {
        responsable_nom: 'contact_comptabilite',
        responsable_telephone: 'telephone_comptabilite',
        responsable_email: 'email_comptabilite',
      },
    };

    const toggles = this.querySelectorAll('[data-mirror-toggle]');
    if (toggles.length === 0) return;

    const applyMirror = (target) => {
      const map = mirrorMap[target];
      if (!map) return;

      for (const [srcName, destName] of Object.entries(map)) {
        const src = this.querySelector(`[name="${srcName}"]`);
        const dest = this.querySelector(`[name="${destName}"]`);
        if (!src || !dest) continue;

        if (srcName === 'responsable_nom' && destName === 'contact_comptabilite') {
          const prenom = this.querySelector('[name="responsable_prenom"]');
          dest.value = [prenom?.value, src.value].filter(Boolean).join(' ').trim();
        } else {
          dest.value = src.value;
        }

        dest.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };

    const setMirrorState = (target, enabled) => {
      const fieldsContainer = this.querySelector(`[data-mirror-fields="${target}"]`);
      if (!fieldsContainer) return;

      const inputs = fieldsContainer.querySelectorAll('input, select, textarea');
      for (const input of inputs) {
        input.disabled = enabled;
      }
      fieldsContainer.classList.toggle('b2b-form-grid--mirrored', enabled);

      if (enabled) applyMirror(target);
    };

    for (const toggle of toggles) {
      const target = toggle.dataset.mirrorToggle;
      setMirrorState(target, toggle.checked);

      toggle.addEventListener('change', () => {
        setMirrorState(target, toggle.checked);
      });
    }

    const sourceNames = ['responsable_nom', 'responsable_prenom', 'responsable_email', 'responsable_telephone'];
    for (const name of sourceNames) {
      const src = this.querySelector(`[name="${name}"]`);
      if (!src) continue;

      src.addEventListener('input', () => {
        for (const toggle of toggles) {
          if (toggle.checked) applyMirror(toggle.dataset.mirrorToggle);
        }
      });
    }
  }

  /**
   * Disable time inputs when "Fermé" checkbox is checked for a day.
   * @returns {void}
   */
  initializeScheduleClosedToggle() {
    const closedToggles = this.querySelectorAll('[data-schedule-closed]');

    for (const toggle of closedToggles) {
      const day = toggle.dataset.scheduleClosed;
      const row = this.querySelector(`[data-schedule-row="${day}"]`);
      const timeInputs = this.querySelectorAll(`[data-schedule-time="${day}"]`);

      const apply = () => {
        const closed = toggle.checked;
        row?.classList.toggle('b2b-schedule__row--closed', closed);
        for (const input of timeInputs) {
          input.disabled = closed;
        }
      };

      apply();
      toggle.addEventListener('change', apply);
    }
  }

  /**
   * Initialize realtime validation on blur
   * @returns {void}
   */
  initializeRealtimeValidation() {
    const fields = this.querySelectorAll('input, select, textarea');
    
    for (const field of fields) {
      field.addEventListener('blur', () => {
        const errorId = `error-${field.id || field.name}`;
        const hasExistingError = document.getElementById(errorId);
        
        if (field.checkValidity()) {
          this.clearFieldError(field);
        } else if (hasExistingError) {
          this.updateFieldError(field);
        }
      });
    }
  }

  /**
   * Update error message for specific field
   * @param {HTMLInputElement} field
   * @returns {void}
   */
  updateFieldError(field) {
    const errorId = `error-${field.id || field.name}`;
    const errorEl = document.getElementById(errorId);
    
    if (errorEl) {
      const newMessage = this.getValidationMessage(field);
      errorEl.textContent = newMessage;
    }
  }

  /**
   * Clear error for specific field
   * @param {HTMLInputElement} field
   * @returns {void}
   */
  clearFieldError(field) {
    const errorId = `error-${field.id || field.name}`;
    const errorEl = document.getElementById(errorId);
    
    if (errorEl) {
      errorEl.remove();
    }

    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
    field.classList.remove('b2b-input--error', 'b2b-textarea--error', 'b2b-select--error');
  }

  /**
   * Bind event listeners
   * @returns {void}
   */
  bindEvents() {
    this.addEventListener('click', (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.dataset.action;

      if (action === 'next') {
        this.handleNext();
      } else if (action === 'prev') {
        this.handlePrev();
      } else if (action === 'submit') {
        this.handleSubmit();
      }
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('input:not([type="submit"])')) {
        e.preventDefault();
        this.handleNext();
      }
    });
  }

  /**
   * Initialize radio toggle functionality
   * @returns {void}
   */
  initializeRadioToggles() {
    const radioInputs = this.querySelectorAll('input[type="radio"][data-toggle-target]');

    for (const radio of radioInputs) {
      radio.addEventListener('change', () => {
        const targetId = radio.dataset.toggleTarget;
        const target = this.querySelector(`#${targetId}`);
        const wrapper = this.querySelector(`#${targetId}-wrapper`);

        if (!target) return;

        if (radio.checked) {
          target.disabled = false;
          if (wrapper) {
            wrapper.hidden = false;
            requestAnimationFrame(() => {
              wrapper.classList.add('b2b-form-field--visible');
            });
          }
          target.focus();
        }
      });

      const name = radio.name;
      const allRadios = this.querySelectorAll(`input[name="${name}"]`);

      for (const otherRadio of allRadios) {
        if (otherRadio !== radio) {
          otherRadio.addEventListener('change', () => {
            const targetId = radio.dataset.toggleTarget;
            const target = this.querySelector(`#${targetId}`);
            const wrapper = this.querySelector(`#${targetId}-wrapper`);
            
            if (target) {
              target.disabled = true;
              target.value = '';
              if (wrapper) {
                wrapper.classList.remove('b2b-form-field--visible');
                wrapper.addEventListener('transitionend', () => {
                  wrapper.hidden = true;
                }, { once: true });
              }
            }
          });
        }
      }
    }

    const selectToggles = this.querySelectorAll('select[data-toggle-select]');
    
    for (const select of selectToggles) {
      select.addEventListener('change', () => {
        const targetId = select.dataset.toggleTarget;
        const toggleValue = select.dataset.toggleValue;
        const targetField = this.querySelector(`[data-toggle-field="${targetId}"]`);
        const targetInput = this.querySelector(`#${targetId}`);

        if (!targetField || !targetInput) return;

        if (select.value === toggleValue) {
          targetField.style.display = '';
          targetInput.disabled = false;
        } else {
          targetField.style.display = 'none';
          targetInput.disabled = true;
          targetInput.value = '';
        }
      });
    }
  }

  /**
   * Initialize file upload functionality
   * @returns {void}
   */
  initializeFileUpload() {
    const uploadInput = this.querySelector('.b2b-upload__input');
    if (!uploadInput) return;

    uploadInput.addEventListener('change', (e) => {
      this.handleFileUpload(e.target.files);
    });

    const uploadZone = this.querySelector('.b2b-upload__zone');
    if (!uploadZone) return;

    uploadZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      uploadZone.classList.add('b2b-upload__zone--dragover');
    });

    uploadZone.addEventListener('dragleave', () => {
      uploadZone.classList.remove('b2b-upload__zone--dragover');
    });

    uploadZone.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadZone.classList.remove('b2b-upload__zone--dragover');
      this.handleFileUpload(e.dataTransfer.files);
    });
  }

  /**
   * Handle file upload
   * @param {FileList} files
   * @returns {void}
   */
  handleFileUpload(files) {
    const filesList = this.querySelector('[data-upload-list]');
    if (!filesList) return;

    const file = files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      this.showError('Seuls les fichiers PDF sont acceptés');
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      this.showError('Le fichier dépasse la taille maximale de 500 Mo');
      return;
    }

    this.uploadedFiles = [file];
    filesList.innerHTML = '';
    this.renderFileTag(file, filesList);
  }

  /**
   * Render file tag
   * @param {File} file
   * @param {HTMLElement} container
   * @returns {void}
   */
  renderFileTag(file, container) {
    const tag = document.createElement('div');
    tag.className = 'b2b-file-tag';
    tag.innerHTML = `
      <span class="b2b-file-tag__icon">📄</span>
      <span class="b2b-file-tag__name">${file.name}</span>
      <button type="button" class="b2b-file-tag__remove" aria-label="Supprimer ${file.name}">×</button>
    `;

    const removeBtn = tag.querySelector('.b2b-file-tag__remove');
    removeBtn.addEventListener('click', () => {
      const index = this.uploadedFiles.indexOf(file);
      if (index > -1) {
        this.uploadedFiles.splice(index, 1);
      }
      tag.remove();
    });

    container.appendChild(tag);
  }

  /**
   * Validate current step
   * @returns {boolean}
   */
  validateCurrentStep() {
    const currentStepEl = this.steps[this.currentStep - 1];
    const inputs = currentStepEl.querySelectorAll('input, select, textarea');

    this.clearAllErrors(currentStepEl);

    let firstInvalidField = null;
    let isValid = true;

    for (const input of inputs) {
      if (!input.checkValidity()) {
        isValid = false;
        this.showFieldError(input);

        if (!firstInvalidField) {
          firstInvalidField = input;
        }
      }
    }

    const uploadZone = currentStepEl.querySelector('.b2b-upload[data-upload-required]');
    if (uploadZone && this.uploadedFiles.length === 0) {
      isValid = false;
      this.showUploadError(uploadZone);

      if (!firstInvalidField) {
        firstInvalidField = uploadZone;
      }
    }

    if (!isValid && firstInvalidField) {
      this.scrollToField(firstInvalidField);
      return false;
    }

    return true;
  }

  /**
   * Clear all error messages in step
   * @param {HTMLElement} stepElement
   * @returns {void}
   */
  clearAllErrors(stepElement) {
    const errorMessages = stepElement.querySelectorAll('.b2b-field-error');
    for (const error of errorMessages) {
      error.remove();
    }

    const invalidFields = stepElement.querySelectorAll('[aria-invalid="true"]');
    for (const field of invalidFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
      field.classList.remove('b2b-input--error', 'b2b-textarea--error', 'b2b-select--error');
    }

    const uploadZone = stepElement.querySelector('.b2b-upload__zone--error');
    if (uploadZone) {
      uploadZone.classList.remove('b2b-upload__zone--error');
    }
  }

  /**
   * Show error message for specific field
   * @param {HTMLInputElement} field
   * @returns {void}
   */
  showFieldError(field) {
    const errorMessage = this.getValidationMessage(field);
    const errorId = `error-${field.id || field.name}`;
    
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', errorId);
    
    const errorClass = field.classList.contains('b2b-textarea')
      ? 'b2b-textarea--error'
      : field.classList.contains('b2b-select')
      ? 'b2b-select--error'
      : 'b2b-input--error';
    
    field.classList.add(errorClass);

    const errorEl = document.createElement('div');
    errorEl.className = 'b2b-field-error';
    errorEl.id = errorId;
    errorEl.textContent = errorMessage;
    errorEl.setAttribute('role', 'alert');

    const fieldContainer = field.closest('.b2b-form-field') || field.parentElement;
    if (fieldContainer) {
      fieldContainer.appendChild(errorEl);
    }
  }

  /**
   * Show error on upload zone
   * @param {HTMLElement} uploadZone
   * @returns {void}
   */
  showUploadError(uploadZone) {
    const errorId = 'error-rib-upload';
    const messages = this.validationMessages;
    const errorMessage = messages.fileMissing || 'Veuillez joindre votre RIB (fichier PDF)';

    const errorEl = document.createElement('div');
    errorEl.className = 'b2b-field-error';
    errorEl.id = errorId;
    errorEl.textContent = errorMessage;
    errorEl.setAttribute('role', 'alert');

    uploadZone.appendChild(errorEl);
    uploadZone.querySelector('.b2b-upload__zone')?.classList.add('b2b-upload__zone--error');
  }

  /**
   * Get validation message for field
   * @param {HTMLInputElement} field
   * @returns {string}
   */
  getValidationMessage(field) {
    const validity = field.validity;
    const messages = this.validationMessages;

    if (validity.valueMissing) {
      if (field.type === 'checkbox') {
        return messages.valueMissing?.checkbox || 'Veuillez cocher cette case obligatoire';
      }
      return messages.valueMissing?.default || 'Ce champ est obligatoire';
    }

    if (validity.typeMismatch) {
      if (field.type === 'email') {
        return messages.typeMismatch?.email || 'Veuillez entrer une adresse e-mail valide';
      }
    }

    if (validity.patternMismatch) {
      const fieldName = field.name || field.id || '';
      
      if (fieldName.includes('code_postal')) {
        return messages.patternMismatch?.code_postal || 'Le code postal doit contenir 5 chiffres';
      }
      if (fieldName.includes('siret')) {
        return messages.patternMismatch?.siret || 'Le SIRET doit contenir 14 chiffres';
      }
      if (fieldName.includes('telephone') || fieldName.includes('tel')) {
        return messages.patternMismatch?.telephone || 'Veuillez entrer un numéro de téléphone valide';
      }
      if (fieldName.includes('iban')) {
        return messages.patternMismatch?.iban || 'Veuillez entrer un IBAN valide';
      }
      if (fieldName.includes('bic')) {
        return messages.patternMismatch?.bic || 'Veuillez entrer un BIC valide';
      }
      
      return messages.patternMismatch?.default || 'Le format de ce champ est invalide';
    }

    if (validity.tooShort) {
      return messages.tooShort || 'Ce champ est trop court';
    }

    if (validity.tooLong) {
      return messages.tooLong || 'Ce champ est trop long';
    }

    return 'Veuillez vérifier ce champ';
  }

  /**
   * Scroll to field with error
   * @param {HTMLElement} field
   * @returns {void}
   */
  scrollToField(field) {
    field.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    field.focus({ preventScroll: true });
  }

  /**
   * Handle next step
   * @returns {void}
   */
  handleNext() {
    if (!this.validateCurrentStep()) return;

    if (this.currentStep < this.totalSteps) {
      this.goToStep(this.currentStep + 1);
    }
  }

  /**
   * Handle previous step
   * @returns {void}
   */
  handlePrev() {
    if (this.currentStep > 1) {
      this.goToStep(this.currentStep - 1);
    }
  }

  /**
   * Go to specific step
   * @param {number} stepNumber
   * @returns {void}
   */
  goToStep(stepNumber) {
    const oldStep = this.currentStep;
    this.currentStep = stepNumber;

    this.steps[oldStep - 1].classList.remove('b2b-form__step--active');
    this.steps[stepNumber - 1].classList.add('b2b-form__step--active');

    this.updateStepper(stepNumber);
    this.scrollToTop();
  }

  /**
   * Update stepper visual state
   * @param {number} currentStep
   * @returns {void}
   */
  updateStepper(currentStep) {
    for (let i = 0; i < this.stepperItems.length; i++) {
      const item = this.stepperItems[i];

      item.classList.remove('b2b-stepper__item--active', 'b2b-stepper__item--completed');

      if (i + 1 < currentStep) {
        item.classList.add('b2b-stepper__item--completed');
      } else if (i + 1 === currentStep) {
        item.classList.add('b2b-stepper__item--active');
      }
    }
  }

  /**
   * Scroll to top of form
   * @returns {void}
   */
  scrollToTop() {
    const mainSection = this.querySelector('.b2b-form__main');
    if (!mainSection) return;

    mainSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Collect form data
   * @returns {Object}
   */
  collectFormData() {
    const formData = {};
    const inputs = this.querySelectorAll('input, select, textarea');

    for (const input of inputs) {
      if (input.type === 'checkbox') {
        formData[input.name] = input.checked;
      } else if (input.type === 'radio') {
        if (input.checked) {
          formData[input.name] = input.value;
        }
      } else if (input.type !== 'file') {
        formData[input.name] = input.value;
      }
    }

    return formData;
  }

  /**
   * Handle form submission
   * @returns {Promise<void>}
   */
  async handleSubmit() {
    if (!this.validateCurrentStep()) return;

    const submitButton = this.querySelector('[data-action="submit"]');
    if (!submitButton) return;

    this.setButtonLoading(submitButton, true);

    try {
      const formData = this.collectFormData();
      const result = await this.sendToN8N(formData);

      if (result.success) {
        this.setButtonSuccess(submitButton);
        setTimeout(() => {
          this.showResultStep(true);
        }, 800);
      } else {
        this.setButtonError(submitButton);
        setTimeout(() => {
          this.showResultStep(false, result.error);
        }, 800);
      }
    } catch (error) {
      this.setButtonError(submitButton);
      setTimeout(() => {
        this.showResultStep(false, error.message);
      }, 800);
    }
  }

  /**
   * Set button to loading state
   * @param {HTMLButtonElement} button
   * @param {boolean} isLoading
   * @returns {void}
   */
  setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('b2b-button--loading');
      
      const messages = this.validationMessages;
      const loadingText = messages.loading || 'Chargement...';
      
      button.innerHTML = `
        <span>${loadingText}</span>
        <span class="b2b-button__spinner" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="12 38" transform="rotate(-90 10 10)">
              <animateTransform attributeName="transform" type="rotate" from="0 10 10" to="360 10 10" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </span>
      `;
    } else {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('b2b-button--loading');
    }
  }

  /**
   * Set button to success state
   * @param {HTMLButtonElement} button
   * @returns {void}
   */
  setButtonSuccess(button) {
    button.classList.remove('b2b-button--loading');
    button.classList.add('b2b-button--success');
    button.setAttribute('aria-label', 'Formulaire envoyé avec succès');
    
    button.innerHTML = `
      <span>Envoyé</span>
      <span class="b2b-button__icon" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.667 5L7.5 14.167L3.333 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `;
  }

  /**
   * Set button to error state
   * @param {HTMLButtonElement} button
   * @returns {void}
   */
  setButtonError(button) {
    button.classList.remove('b2b-button--loading');
    button.classList.add('b2b-button--error');
    button.setAttribute('aria-label', 'Erreur lors de l\'envoi');
    
    button.innerHTML = `
      <span>Erreur</span>
      <span class="b2b-button__icon" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 6v4m0 4h.01M19 10a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `;
  }

  /**
   * Show result step
   * @param {boolean} isSuccess
   * @param {string} errorMessage
   * @returns {void}
   */
  showResultStep(isSuccess, errorMessage = '') {
    const resultStep = this.querySelector('.b2b-form__step--result');
    if (!resultStep) return;

    const successContent = resultStep.querySelector('[data-result="success"]');
    const errorContent = resultStep.querySelector('[data-result="error"]');
    const errorMessageEl = resultStep.querySelector('[data-error-message]');

    if (isSuccess && successContent) {
      successContent.style.display = '';
      if (errorContent) errorContent.style.display = 'none';
    } else if (!isSuccess && errorContent) {
      errorContent.style.display = '';
      if (successContent) successContent.style.display = 'none';
      
      if (errorMessageEl && errorMessage) {
        errorMessageEl.textContent = errorMessage;
      }
    }

    const currentStepEl = this.steps[this.currentStep - 1];
    currentStepEl.classList.remove('b2b-form__step--active');
    resultStep.classList.add('b2b-form__step--active');

    this.scrollToTop();
    
    this.dispatchEvent(
      new CustomEvent('b2b-form:completed', {
        bubbles: true,
        detail: { success: isSuccess, error: errorMessage },
      }),
    );
  }

  /**
   * Send data to n8n webhook
   * @param {Object} formData
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async sendToN8N(formData) {
    if (!this.webhookUrl) {
      const messages = this.validationMessages;
      return {
        success: false,
        error: messages.error_no_webhook || 'URL de webhook non configurée',
      };
    }

    try {
      const formDataToSend = new FormData();

      for (const [key, value] of Object.entries(formData)) {
        if (typeof value === 'object' && value !== null) {
          formDataToSend.append(key, JSON.stringify(value));
        } else {
          formDataToSend.append(key, value);
        }
      }

      if (this.uploadedFiles.length > 0) {
        formDataToSend.append('rib_fichier', this.uploadedFiles[0]);
      }

      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        let errorMessage = `Erreur ${response.status}`;
        
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          errorMessage = `${errorMessage}: ${response.statusText}`;
        }

        return {
          success: false,
          error: errorMessage,
        };
      }

      return { success: true };
    } catch (error) {
      const messages = this.validationMessages;
      return {
        success: false,
        error: messages.error_network || 'Erreur de connexion au serveur',
      };
    }
  }
}

customElements.define('b2b-form-stepper', B2BFormStepper);
